import DairyService from '@src/api/services/dairy';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { StateEl } from '../style';
import { FormFields } from '@src/components/FormFields';
import { GlobalEl } from '@src/styles/app';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import notify from '~/components/Notify';
import { useMutation } from 'react-query';
import Drawer from '~/components/Drawer';
import { useAuth } from '~/routes/components/AuthProvider';
import { MenuItem, Tab } from 'semantic-ui-react';
import styled from 'styled-components';
import { StyledTabPane } from '~/pages/Dairy/Settings/General/style';

interface Props {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
  fetchData?: () => void,
  idProductionZone?: any
}

const StyledMenuItem = styled(MenuItem)`
 .error {
  color: red;
 }
`

const CreateEditProductionZone: React.FunctionComponent<Props> = (props) => {
  const { isVisible, handleHiddenDrawer, idProductionZone } = props;
  const { t } = useTranslation();
  const { systemData: { languages } } = useAuth()
  const { handleSubmit, control, reset, formState } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (idProductionZone) {
      editProductionZone(idProductionZone);
    } else {
      reset({
        languages: getLangs()
      });
    }
  }, [isVisible, idProductionZone]);

  function getLangs() {
    return languages.map(language => ({
      id: language.id,
      name: '',
      abbreviation: ''
    }))
  }

  function getParsedLanguages(statusLangs) {
    languages.map((language) => {
      const index = statusLangs.findIndex(languageText => languageText.id === language.id)
      if (index < 0) {
        statusLangs.push({
          id: language.id,
          name: '',
          abbreviation: '',
          order: language.order
        })
      } else {
        statusLangs[index].order = language.order
      }
    })
    statusLangs.sort((a, b) => a.order - b.order)
    statusLangs?.forEach(lang => delete lang.order)
    return statusLangs
  }

  const onSuccess = () => {
    notify(t('Successfully created production zone'), 'success');
    props.fetchData()
  };

  const onProductionZoneSuccess = (response) => {
    response.data.languages = getParsedLanguages(response.data.languages)
    reset(response.data)
  }

  const { mutateAsync: storeProductionZone } =
    DairyQuery.storeProductionZone(onSuccess);

  const { mutate: updateProductionZone } = useMutation(
    'production-zones-update',
    DairyService.updateProductionZone,
    {
      onSuccess: () => {
        notify(t('Successfully updated production zone'), 'success');
        props.fetchData()
      }
    }
  );

  const { isLoading: isProductionZoneLoading, mutateAsync: editProductionZone } =
    DairyQuery.editProductionZone(onProductionZoneSuccess);

  const onSubmit = (data) => {
    if (!idProductionZone) {
      storeProductionZone({ ...data });
    } else {
      updateProductionZone({ id: idProductionZone, data })
    }
    handleHiddenDrawer();
  };

  function errorTab() {
    //@ts-ignore
    return formState.errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number')
  }

  function getMenuItem(label, index) {
    const errors = errorTab() || []

    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLanguageFields() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      panes={
        languages.map((language) => {
          const indexLanguage = languages.findIndex(languageIndex => languageIndex['id'] === language.id)
          return {
            //@ts-ignore
            menuItem: getMenuItem(language['label'], indexLanguage),
            pane: <StyledTabPane key={language.id}>
              <FormFields.Input
                control={control}
                name={`languages[${indexLanguage}].name`}
                label={t('Production zone name')}
                placeholder={t('Production zone name')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter Production zone name')
                  }
                }}
              />
              <FormFields.Input
                control={control}
                label={t('Production zone abbreviation')}
                placeholder={t('Production zone abbreviation')}
                name={`languages[${indexLanguage}].abbreviation`}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter Production zone abbreviation')
                  },
                  maxLength: {
                    value: 20,
                    message: t('Max. 20 characters')
                  }
                }}
              />
            </StyledTabPane>
          }
        })}
    />
  }

  const renderContent = () => {
    return (
      <>
        {renderLanguageFields()}
        <StateEl.DrawerFooter>
          <GlobalEl.PrimaryButton
            disabled={Object.keys(formState.dirtyFields).length === 0}
          >{idProductionZone ? t('Update') : t('Add')}</GlobalEl.PrimaryButton>
        </StateEl.DrawerFooter>
      </>
    );
  };

  return <Drawer
    isVisible={isVisible}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isProductionZoneLoading}
    header={idProductionZone ? t('Edit production zone') : t('Create production zone')}
    closeOnEscape
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}
    content={renderContent()}
  />
};

export default CreateEditProductionZone;
